import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, MapPin, Zap, Droplets, Instagram, ExternalLink, Calendar, Star, Store, Search, Bell, FileCheck, CheckCircle2, AlertCircle, FileText, Upload, Image as ImageIcon, Globe, Truck, Users, Euro, BookOpen, Quote, Play, ClipboardList, Edit3, TrendingUp, Award, ChevronRight, Gift } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { NotificationBell } from '../components/NotificationBell';
import { UserMenu } from '../components/UserMenu';
import { Logo } from '../components/Logo';
import { DocumentDropzone } from '../components/DocumentDropzone';
import { CalendarWidget } from '../components/CalendarWidget';
import { toast } from 'react-hot-toast';

interface CompletionDetail {
  pct: number;
  criticalMissing: string[];
  recommendedMissing: string[];
}

function computeCompletion(data: any): CompletionDetail {
  const critical: [string, any][] = [
    ['Nom du stand', data.name],
    ['Type de stand', data.standType],
    ['Description', data.description],
    ['Région', data.region],
    ['Logo', data.logo],
    ['Bannière', data.bannerUrl],
  ];
  const recommended: [string, any][] = [
    ['Histoire de la marque', data.brandStory],
    ['Galerie photos', data.galleryUrls?.length > 0],
    ['Réseaux sociaux', data.instagram || data.tiktok],
    ['Expérience', data.experienceRange],
    ['Festivals passés', data.pastFestivals],
    ['Assurance RC Pro', data.insuranceUrl],
    ['KBIS / SIRET', data.kbisUrl],
    ['Gamme de prix', data.priceRange],
  ];
  const criticalPct = critical.filter(([, v]) => Boolean(v)).length / critical.length;
  const recommendedPct = recommended.filter(([, v]) => Boolean(v)).length / recommended.length;
  return {
    pct: Math.round((criticalPct * 0.65 + recommendedPct * 0.35) * 100),
    criticalMissing: critical.filter(([, v]) => !v).map(([label]) => label),
    recommendedMissing: recommended.filter(([, v]) => !v).map(([label]) => label),
  };
}

export default function StandProfilePage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [standData, setStandData] = useState<any>(null);
  const [stats, setStats] = useState({ total: 0, confirmed: 0, pending: 0 });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchStand = async () => {
      if (user) {
        try {
          const docRef = doc(db, 'stands', user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const base = docSnap.data();
            // Documents légaux : sous-collection privée (repli sur l'ancien emplacement inline).
            // Lecture isolée : si elle échoue (ex. règles Firestore pas encore
            // déployées), on affiche quand même le profil avec les données de base
            // au lieu de figer la page sur « Chargement… ».
            let priv: any = {};
            try {
              const privSnap = await getDoc(doc(db, 'stands', user.uid, 'private', 'documents'));
              priv = privSnap.exists() ? (privSnap.data() as any) : {};
            } catch (privErr) {
              console.warn("Documents privés du stand illisibles (règles Firestore à déployer ?)", privErr);
            }
            setStandData({
              ...base,
              insuranceUrl: priv.insuranceUrl ?? base.insuranceUrl ?? '',
              kbisUrl: priv.kbisUrl ?? base.kbisUrl ?? '',
              sanitaryUrl: priv.sanitaryUrl ?? base.sanitaryUrl ?? '',
            });
          } else {
            // No stand data found
            navigate('/my-stand');
          }
        } catch (error) {
          console.error("Error fetching stand data", error);
        }
      }
    };
    fetchStand();
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchStats = async () => {
      try {
        const q = query(collection(db, 'applications'), where('applicantId', '==', user.uid));
        const snap = await getDocs(q);
        let total = 0, confirmed = 0, pending = 0;
        snap.forEach(d => {
          total++;
          const s = d.data().status;
          if (s === 'confirme') confirmed++;
          else if (s === 'approved' || s === 'en_attente') pending++;
        });
        setStats({ total, confirmed, pending });
      } catch {}
    };
    fetchStats();
  }, [user]);

  const handleUploadComplete = async (field: string, url: string) => {
    if (!user) return;
    try {
      // Documents légaux : écrits dans la sous-collection privée (merge pour préserver allowedViewers).
      await setDoc(
        doc(db, 'stands', user.uid, 'private', 'documents'),
        { [field]: url, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setStandData((prev: any) => ({ ...prev, [field]: url }));
      toast.success("Document mis à jour !");
    } catch (error) {
      console.error("Error updating document", error);
      toast.error("Erreur lors de la mise à jour du document.");
    }
  };

  if (!standData) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white">Chargement...</div>;
  }

  const completion = computeCompletion(standData);
  const completionPct = completion.pct;

  const standTypeLabels: Record<string, string> = {
    'food': 'Food Truck / Restauration Chaude',
    'snacking': 'Snacking / Restauration Froide',
    'craft': 'Artisanat / Créateurs',
    'fashion': 'Mode / Friperie / Vêtements',
    'vinyls': 'Vinyles',
    'tattoo': 'Tatouage / Piercing / Paillettes',
    'associative': 'Engagé / Associatif',
    'partners': 'Partenaires / Sponsors',
    'service': 'Services / Bien-être',
    'other': 'Autre'
  };

  const experienceLabels: Record<string, string> = {
    'none': 'Premier festival ! 🐣',
    '1-3': 'Émergeant (1-3 festivals) 🌱',
    '4-10': 'Confirmé (4-10 festivals) 🚀',
    '11+': 'Expert (+10 festivals) 🏆'
  };

  const stand = {
    name: standData.name || '',
    category: standData.standType ? standTypeLabels[standData.standType] : (standData.activity || ''),
    description: standData.description || '',
    brandStory: standData.brandStory || '',
    foundingYear: standData.foundingYear || null,
    coverImage: standData.bannerUrl || (standData.images?.[0] || ''),
    gallery: standData.galleryUrls?.length > 0 ? standData.galleryUrls : [],
    videoUrl: standData.videoUrl || '',
    pressKit: standData.pressKitUrl || '',
    pastFestivals: standData.pastFestivals || '',
    organizerTestimonial: standData.organizerTestimonial || '',
    priceRange: standData.priceRange || '',
    logistics: {
      dimensions: standData.width && standData.depth ? `${standData.width}m × ${standData.depth}m` : '',
      electricity: standData.needsElectricity ? (standData.powerKw ? `${standData.powerKw} kW` : 'Oui') : 'Non',
      water: standData.waterNeeds ? 'Oui' : 'Non',
      hasVehicle: standData.hasVehicle || false,
      vehicleDetails: standData.vehicleDetails || '',
      furnitureNeeds: standData.furnitureNeeds || '',
    },
    socials: {
      instagram: standData.instagram || '',
      tiktok: standData.tiktok || '',
    },
    documents: [
      { id: 'insuranceUrl', label: 'Assurance RC Pro', url: standData.insuranceUrl, path: `stands/${user?.uid}/doc_assurance` },
      { id: 'kbisUrl', label: 'Kbis / SIRET', url: standData.kbisUrl, path: `stands/${user?.uid}/doc_kbis` },
      { id: 'sanitaryUrl', label: 'Agréments Sanitaires', url: standData.sanitaryUrl, path: `stands/${user?.uid}/doc_sanitaire` },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0d0e12] text-white font-sans pb-24">

      {/* ── Dashboard cockpit ── */}
      <div className="bg-[#080909] border-b border-white/5 pt-14 md:pt-16">
        <div className="max-w-6xl mx-auto px-5 py-6">

          {/* Greeting + completion */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              {standData.logo
                ? <img src={standData.logo} className="w-12 h-12 rounded-xl object-cover border border-white/10" alt="logo" />
                : <div className="w-12 h-12 rounded-xl bg-[#FF5C00]/15 border border-[#FF5C00]/20 flex items-center justify-center"><Store className="w-6 h-6 text-[#FF5C00]" /></div>
              }
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-widest">Tableau de bord exposant</p>
                <h1 className="text-xl font-black text-white leading-tight">{standData.name || 'Mon Stand'}</h1>
              </div>
            </div>

            {/* Completion score badge */}
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-black uppercase tracking-widest ${
                    completionPct >= 80 ? 'text-emerald-400' : completionPct >= 50 ? 'text-amber-400' : 'text-red-400'
                  }`}>
                    {completionPct >= 80 ? 'Profil complet' : completionPct >= 50 ? 'À compléter' : 'Profil incomplet'}
                  </span>
                  <span className={`text-lg font-black ${
                    completionPct >= 80 ? 'text-emerald-400' : completionPct >= 50 ? 'text-amber-400' : 'text-red-400'
                  }`}>{completionPct}%</span>
                </div>
                <div className="w-40 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${
                      completionPct >= 80 ? 'bg-emerald-400' : completionPct >= 50 ? 'bg-amber-400' : 'bg-red-400'
                    }`}
                    style={{ width: `${completionPct}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-white/3 border border-white/8 rounded-2xl p-4 flex flex-col gap-1">
              <span className="text-2xl font-black text-white">{stats.total}</span>
              <span className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider">Candidatures</span>
            </div>
            <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-2xl p-4 flex flex-col gap-1">
              <span className="text-2xl font-black text-emerald-400">{stats.confirmed}</span>
              <span className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider">Confirmées</span>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/15 rounded-2xl p-4 flex flex-col gap-1">
              <span className="text-2xl font-black text-amber-400">{stats.pending}</span>
              <span className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider">En attente</span>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate('/home')}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#FF5C00] hover:bg-[#eb5500] text-black text-sm font-black rounded-xl transition-all active:scale-95 shadow-[0_0_20px_rgba(255,92,0,0.25)]"
            >
              <Search className="w-4 h-4" />
              Parcourir les festivals
            </button>
            <button
              onClick={() => navigate('/my-stand')}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/8 hover:bg-white/15 border border-white/10 text-white text-sm font-bold rounded-xl transition-all"
            >
              <Edit3 className="w-4 h-4" />
              Modifier mon profil
            </button>
            <button
              onClick={() => navigate('/stand/festivals')}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/8 hover:bg-white/15 border border-white/10 text-white text-sm font-bold rounded-xl transition-all"
            >
              <ClipboardList className="w-4 h-4" />
              Mes candidatures
              {stats.pending > 0 && (
                <span className="bg-amber-400 text-black text-[10px] font-black px-1.5 py-0.5 rounded-full">{stats.pending}</span>
              )}
            </button>
            <button
              onClick={() => navigate('/stand/parrainage')}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#FF5C00]/10 hover:bg-[#FF5C00]/20 border border-[#FF5C00]/25 text-[#FF5C00] text-sm font-bold rounded-xl transition-all"
            >
              <Gift className="w-4 h-4" />
              Parrainage
            </button>
          </div>

          {/* Completion breakdown — CRITIQUE vs RECOMMANDÉ */}
          {(completion.criticalMissing.length > 0 || completion.recommendedMissing.length > 0) && (
            <div className="mt-4 space-y-2">
              {completion.criticalMissing.length > 0 && (
                <button
                  onClick={() => navigate('/my-stand')}
                  className="w-full flex items-start justify-between gap-3 p-3 bg-red-500/5 border border-red-500/25 rounded-xl hover:bg-red-500/10 transition-all group text-left"
                >
                  <div className="flex items-start gap-2.5 min-w-0">
                    <span className="shrink-0 text-[9px] font-black uppercase tracking-widest bg-red-500/20 text-red-400 px-2 py-1 rounded-md mt-0.5">Critique</span>
                    <span className="text-xs text-gray-300 leading-relaxed">
                      Sans ces champs, votre profil n'apparaît pas correctement :{' '}
                      <span className="text-red-300 font-semibold">{completion.criticalMissing.join(', ')}</span>
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-red-400 shrink-0 group-hover:translate-x-1 transition-transform mt-1" />
                </button>
              )}
              {completion.recommendedMissing.length > 0 && (
                <button
                  onClick={() => navigate('/my-stand')}
                  className="w-full flex items-start justify-between gap-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl hover:bg-amber-500/10 transition-all group text-left"
                >
                  <div className="flex items-start gap-2.5 min-w-0">
                    <span className="shrink-0 text-[9px] font-black uppercase tracking-widest bg-amber-500/15 text-amber-400 px-2 py-1 rounded-md mt-0.5">Recommandé</span>
                    <span className="text-xs text-gray-400 leading-relaxed">
                      Pour maximiser vos chances de sélection :{' '}
                      <span className="text-amber-300/90 font-medium">{completion.recommendedMissing.join(', ')}</span>
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-amber-400 shrink-0 group-hover:translate-x-1 transition-transform mt-1" />
                </button>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ── Profile view ── */}

      {/* Cover Image */}
      <div className="w-full pt-4 flex justify-center">
        <div className="relative w-full max-w-3xl mx-4 md:mx-8 h-[35vh] md:h-[45vh] rounded-2xl overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${stand.coverImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0e12]/80 via-black/30 to-transparent" />
        </div>
      </div>

      {/* Identity block — below the cover */}
      <div className="max-w-6xl mx-auto px-5 -mt-6 relative z-10">
        <div className="flex items-end justify-between gap-4 mb-3">
          <div className="flex items-center gap-4">
            {standData.logo && (
              <div className="w-14 h-14 md:w-20 md:h-20 rounded-2xl overflow-hidden border-2 border-white/20 bg-black shrink-0">
                <img src={standData.logo} alt="Logo" className="w-full h-full object-cover" />
              </div>
            )}
            <div>
              <div className="inline-block px-3 py-1 rounded-full border border-white/15 bg-white/5 text-xs font-semibold tracking-wider text-gray-400 uppercase mb-2">
                {stand.category}
              </div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
                {stand.name}
              </h1>
            </div>
          </div>
          <button
            onClick={() => navigate('/my-stand')}
            className="shrink-0 flex items-center gap-2 text-sm font-bold bg-white/10 hover:bg-white/20 border border-white/10 text-white px-4 py-2.5 rounded-xl transition-colors"
          >
            <Edit3 className="w-4 h-4" /> Modifier
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3">
          {standData.experienceRange && (
            <div className="flex items-center text-[10px] font-black uppercase tracking-widest text-[#FF5C00] bg-[#FF5C00]/10 px-3 py-1 rounded-full border border-[#FF5C00]/20">
              <Star className="w-3 h-3 mr-1.5 fill-current" />
              {experienceLabels[standData.experienceRange] || standData.experienceRange}
            </div>
          )}
          {standData.region && (
            <div className="flex items-center text-sm text-gray-400">
              <MapPin className="w-4 h-4 mr-1.5 text-[#FF5C00]" />
              {standData.region}
            </div>
          )}
          {stand.foundingYear > 1900 && (
            <div className="flex items-center text-sm text-gray-400">
              <Calendar className="w-4 h-4 mr-1.5 text-gray-500" />
              Depuis {stand.foundingYear}
            </div>
          )}
          {stand.socials.instagram && (
            <a href={`https://instagram.com/${stand.socials.instagram.replace('@', '')}`} target="_blank" rel="noreferrer"
               className="flex items-center text-sm text-gray-400 hover:text-pink-400 transition-colors">
              <Instagram className="w-4 h-4 mr-1.5 text-pink-400" />
              {stand.socials.instagram}
            </a>
          )}
          {stand.socials.tiktok && (
            <a href={`https://tiktok.com/${stand.socials.tiktok.startsWith('@') ? stand.socials.tiktok : '@' + stand.socials.tiktok}`} target="_blank" rel="noreferrer"
               className="flex items-center text-sm text-gray-400 hover:text-white transition-colors">
              <span className="text-xs font-black mr-1.5 text-gray-500">TT</span>
              {stand.socials.tiktok}
            </a>
          )}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-5 pt-8">
        <div className="flex flex-col lg:flex-row gap-12">
          
          {/* Main Column */}
          <div className="flex-1 space-y-12">
            
            {/* Description */}
            {stand.description && (
              <section>
                <h2 className="text-2xl font-black mb-6 flex items-center">
                  <Store className="w-6 h-6 mr-3 text-[#FF5C00]" />
                  À propos
                </h2>
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#FF5C00]/20 to-transparent rounded-[2rem] blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
                  <p className="relative text-gray-300 text-lg leading-relaxed bg-[#1A1A1A] p-6 md:p-8 rounded-[2rem] border border-white/5">
                    {stand.description}
                  </p>
                </div>
              </section>
            )}

            {/* Brand Story */}
            {stand.brandStory && (
              <section>
                <h2 className="text-2xl font-black mb-6 flex items-center">
                  <BookOpen className="w-6 h-6 mr-3 text-amber-400" />
                  Notre histoire
                </h2>
                <div className="bg-[#1A1A1A] p-6 md:p-8 rounded-[2rem] border border-white/5">
                  <p className="text-gray-300 text-base leading-relaxed whitespace-pre-line">{stand.brandStory}</p>
                </div>
              </section>
            )}

            {/* Gallery */}
            {stand.gallery.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-black flex items-center">
                    <ImageIcon className="w-6 h-6 mr-3 text-purple-400" />
                    Galerie Photos
                  </h2>
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">{stand.gallery.length} Photos</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {stand.gallery.map((img, idx) => (
                    <div key={idx} className="aspect-square rounded-2xl overflow-hidden group border border-white/10 bg-white/5">
                      <img
                        src={img}
                        alt={`Gallery ${idx}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Video */}
            {stand.videoUrl && (
              <section>
                <h2 className="text-2xl font-black mb-6 flex items-center">
                  <Play className="w-6 h-6 mr-3 text-red-400" />
                  Vidéo de présentation
                </h2>
                <div className="rounded-[2rem] overflow-hidden border border-white/10 bg-black">
                  <video src={stand.videoUrl} controls className="w-full max-h-96 object-contain" />
                </div>
              </section>
            )}

            {/* Past festivals & experience */}
            {stand.pastFestivals && (
              <section>
                <h2 className="text-2xl font-black mb-6 flex items-center">
                  <Calendar className="w-6 h-6 mr-3 text-blue-400" />
                  Expériences festivals
                </h2>
                <div className="bg-[#1A1A1A] p-6 md:p-8 rounded-[2rem] border border-white/5">
                  <p className="text-gray-300 text-base leading-relaxed whitespace-pre-line">{stand.pastFestivals}</p>
                </div>
              </section>
            )}

            {/* Organizer testimonial */}
            {stand.organizerTestimonial && (
              <section className="bg-[#FF5C00]/5 border border-[#FF5C00]/15 rounded-[2rem] p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <Quote className="w-8 h-8 text-[#FF5C00] shrink-0 mt-1" />
                  <div>
                    <p className="text-base text-gray-200 leading-relaxed italic">"{stand.organizerTestimonial}"</p>
                    <p className="text-xs font-bold text-[#FF5C00] uppercase tracking-widest mt-3">— Témoignage d'organisateur</p>
                  </div>
                </div>
              </section>
            )}

            {/* Press Kit */}
            {stand.pressKit && (
              <section className="bg-indigo-500/5 rounded-[2.5rem] p-8 border border-indigo-500/10">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-black flex items-center">
                      <FileText className="w-6 h-6 mr-3 text-indigo-400" />
                      Dossier de Présentation
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">Menu, Press Kit et informations complémentaires</p>
                  </div>
                  <a
                    href={stand.pressKit}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 md:mt-0 inline-flex items-center bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg shadow-indigo-500/20 active:scale-95"
                  >
                    <Upload className="w-4 h-4 mr-2 rotate-180" />
                    Consulter le document
                  </a>
                </div>
              </section>
            )}

            {/* Administrative Documents */}
            <section className="bg-white/5 p-8 rounded-[2.5rem] border border-white/5">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-black flex items-center">
                    <FileCheck className="w-6 h-6 mr-3 text-emerald-400" />
                    Dossier Administratif
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">Vos documents obligatoires pour les festivals</p>
                </div>
                <div className="mt-4 md:mt-0 flex items-center bg-black/20 px-4 py-2 rounded-full border border-white/5">
                  <div className={`w-2 h-2 rounded-full mr-2 ${stand.documents.every(d => d.url) ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    {stand.documents.filter(d => d.url).length} / {stand.documents.length} Complété
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {stand.documents.map((doc) => (
                  <div key={doc.id} className="space-y-3">
                    <DocumentDropzone 
                      label={doc.label}
                      storagePath={doc.path}
                      existingUrl={doc.url}
                      onUploadComplete={(url) => handleUploadComplete(doc.id, url)}
                    />
                  </div>
                ))}
              </div>
              
              {!stand.documents.every(d => d.url) && (
                <div className="mt-8 flex items-start space-x-3 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
                  <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-500 font-medium">
                    Certains documents obligatoires sont manquants. Votre profil ne pourra pas être validé pour les festivals premium tant que votre dossier n'est pas complet.
                  </p>
                </div>
              )}
            </section>

          </div>

          {/* Sidebar / Logistics */}
          <div className="w-full lg:w-[340px] shrink-0">
            <div className="sticky top-28 bg-[#1A1A1A] backdrop-blur-xl rounded-[2rem] p-8 border border-[#FF5C00]/20 shadow-[0_8px_30px_rgb(255,92,0,0.05)] relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF5C00]/5 blur-[50px] -mr-16 -mt-16 pointer-events-none"></div>
              <h3 className="font-black text-white text-xl mb-8 flex items-center relative z-10">
                <Zap className="w-5 h-5 mr-3 text-[#FF5C00]" />
                Fiche Technique
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="text-sm text-gray-500 block mb-1">Dimensions au sol</label>
                  <p className="font-semibold text-lg">{stand.logistics.dimensions}</p>
                </div>
                
                <div className="h-px w-full bg-white/10"></div>
                
                {standData.needsElectricity && (
                  <>
                    <div>
                      <label className="text-sm text-gray-500 block mb-1">Puissance Électrique</label>
                      <div className="flex items-center">
                        <Zap className="w-4 h-4 text-[#FF5C00] mr-2" />
                        <p className="font-bold text-lg text-[#FF5C00]">{stand.logistics.electricity}</p>
                      </div>
                    </div>
                    <div className="h-px w-full bg-white/10"></div>
                  </>
                )}

                <div>
                  <label className="text-sm text-gray-500 block mb-1">Point d'Eau</label>
                  <div className="flex items-center">
                    <Droplets className={`w-4 h-4 mr-2 ${standData.waterNeeds ? 'text-blue-400' : 'text-gray-600'}`} />
                    <p className={`font-semibold text-lg ${standData.waterNeeds ? 'text-blue-400' : 'text-gray-500'}`}>{stand.logistics.water}</p>
                  </div>
                </div>

                {stand.logistics.hasVehicle && (
                  <>
                    <div className="h-px w-full bg-white/10"></div>
                    <div>
                      <label className="text-sm text-gray-500 block mb-1">Véhicule sur place</label>
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-gray-400" />
                        <p className="font-semibold text-white">{stand.logistics.vehicleDetails || 'Oui'}</p>
                      </div>
                    </div>
                  </>
                )}

                {stand.logistics.furnitureNeeds && (
                  <>
                    <div className="h-px w-full bg-white/10"></div>
                    <div>
                      <label className="text-sm text-gray-500 block mb-1">Besoins mobilier</label>
                      <p className="text-sm text-gray-300 leading-relaxed">{stand.logistics.furnitureNeeds}</p>
                    </div>
                  </>
                )}

                {stand.priceRange && (
                  <>
                    <div className="h-px w-full bg-white/10"></div>
                    <div>
                      <label className="text-sm text-gray-500 block mb-1">Gamme de prix</label>
                      <div className="flex items-center gap-2">
                        <Euro className="w-4 h-4 text-amber-400" />
                        <p className="font-semibold text-amber-400">
                          {stand.priceRange === 'low' ? '€ — Petits prix' : stand.priceRange === 'mid' ? '€€ — Prix moyen' : stand.priceRange === 'high' ? '€€€ — Premium' : stand.priceRange}
                        </p>
                      </div>
                    </div>
                  </>
                )}

                <div className="h-px w-full bg-white/10"></div>

                <div>
                  <label className="text-sm text-gray-500 block mb-1">Équipe & Accès</label>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <p className="font-semibold text-lg text-white">{standData.staffBadges || 0} personne{(standData.staffBadges || 0) > 1 ? 's' : ''}</p>
                  </div>
                  {standData.staffNames && (
                    <div className="mt-2 p-3 bg-black/20 rounded-xl border border-white/5">
                      <p className="text-xs text-gray-400 leading-relaxed italic">{standData.staffNames}</p>
                    </div>
                  )}
                </div>
              </div>


            </div>

            {/* Calendrier festivals confirmés */}
            {user && <CalendarWidget userId={user.uid} />}
          </div>

        </div>
      </main>
    </div>
  );
}
